import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  fetchAllLocations,
  addLocation,
  updateLocation,
  deleteLocation,
} from '../actions/location.action'
import { Clinic } from '@dataTypes/location.type'

interface LocationState {
  data: Clinic[] | []
  loading: boolean
  error: string | null
}

const initialState: LocationState = {
  data: [],
  loading: false,
  error: null,
}

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchAllLocations.fulfilled,
      (state, action: PayloadAction<Clinic[]>) => {
        state.data = action.payload
        state.loading = false
      },
    )
    builder.addCase(fetchAllLocations.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchAllLocations.rejected, (state) => {
      state.error = 'Failed to load appointments'
      state.loading = false
    })

    builder.addCase(
      addLocation.fulfilled,
      (state, action: PayloadAction<Clinic>) => {
        state.data = state.data
          ? [...state.data, action.payload]
          : [action.payload]
        state.loading = false
      },
    )

    builder.addCase(
      updateLocation.fulfilled,
      (state, action: PayloadAction<{ id: string; body: Partial<Clinic> }>) => {
        if (!state.data) return

        const index = state.data.findIndex(
          (location) => location.clinic_id === action.payload.id,
        )

        if (index !== -1) {
          const updatedAppointment = {
            ...state.data[index],
            ...action.payload.body,
          }

          state.data[index] = updatedAppointment
        }
        state.loading = false
      },
    )

    builder.addCase(
      deleteLocation.fulfilled,
      (state, action: PayloadAction<string>) => {
        if (!state.data) return
        state.data = state.data.filter(
          (location) => location.clinic_id !== action.payload,
        )
        state.loading = false
      },
    )

    builder.addMatcher(
      (action) =>
        action.type.startsWith('location/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      },
    )
    builder.addMatcher(
      (action) =>
        action.type.startsWith('location/') &&
        action.type.endsWith('/rejected'),
      (state) => {
        state.error = 'Failed to process request'
        state.loading = false
      },
    )
  },
})

export default locationSlice.reducer
